// Dashboard view-model + adapter.
//
// The dashboard's panels were built against a flat mock `Order` shape that
// embedded everything they needed (customer name, a service date, scheduled
// tasks, driver/vehicle/POD flags). The real TMS API splits this up: `Order`
// only carries ids (`customerPartyId`, no embedded tasks, no service date),
// tasks live behind a separate `/orderTasks` call, and customer names come
// from the Directory parties endpoint.
//
// Rather than thread all those lookups through every panel, we resolve them
// once here into a small `DashboardOrder` read-model the panels consume. This
// keeps the panels presentational and pins the real-API coupling to one file.
//
// Notes on fields that have NO direct backing in the real API:
//   • Driver / vehicle assignment — not modelled on orders or tasks (only on
//     ferry bookings). The old "Unassigned" KPI + driver/vehicle "Needs
//     Attention" panel are therefore repurposed to "Unscheduled": active
//     orders that have no tasks planned yet (genuinely need an operator).
//   • Service date — orders have none; we derive it as the earliest task
//     planned date so "Recent Orders" still shows a meaningful date.
//   • Missing POD — derived from each task's `podStatus`.

import type { Address, Party } from '@my/directory-api';
import type { Order, OrderStatusValue, OrderTask } from '@my/tms-api';

// ─── Status presentation (keyed by the real OrderStatusValue) ────────────────

export const DASHBOARD_STATUS_ORDER: OrderStatusValue[] = [
  'Draft',
  'Open',
  'InProgress',
  'Completed',
  'Cancelled',
];

export const DASHBOARD_STATUS_LABELS: Record<OrderStatusValue, string> = {
  Draft: 'Draft',
  Open: 'Open',
  InProgress: 'In Progress',
  Completed: 'Completed',
  Cancelled: 'Cancelled',
};

/** Full Tailwind class string for status badges (bg + text). */
export const DASHBOARD_STATUS_STYLE: Record<OrderStatusValue, string> = {
  Draft: 'bg-slate-100 text-slate-600',
  Open: 'bg-blue-100 text-blue-700',
  InProgress: 'bg-orange-100 text-orange-700',
  Completed: 'bg-green-100 text-green-700',
  Cancelled: 'bg-red-100 text-red-600',
};

/** Active (non-terminal) statuses — used for the "Unscheduled" / attention lens. */
const ACTIVE_STATUSES: readonly OrderStatusValue[] = ['Draft', 'Open', 'InProgress'];

// ─── View model ──────────────────────────────────────────────────────────────

export interface DashboardTask {
  /** ISO date "YYYY-MM-DD" (from the task's planned date). */
  scheduledDate: string | null;
  /** "HH:mm" (from the task's planned time). */
  timeFrom: string | null;
  /** Resolved address book name. */
  locationName: string | null;
  /** Resolved address book locality. */
  city: string | null;
}

export interface DashboardOrder {
  id: string;
  jobNumber: string;
  status: OrderStatusValue;
  customer: string;
  /** Earliest planned task date, or null when nothing is scheduled. */
  serviceDate: string | null;
  /** ISO instant the order was created — drives "Recent Orders" sort. */
  createdAt: string;
  tasks: DashboardTask[];
  /** True when any task still needs a POD signed (`Required` / `Pending`). */
  missingPod: boolean;
}

// ─── Adapter ─────────────────────────────────────────────────────────────────

/** "HH:mm:ss" → "HH:mm"; null/empty stays null. */
function trimTime(time: string | null | undefined): string | null {
  return time ? time.slice(0, 5) : null;
}

/**
 * Resolve the live `Order` list against its per-order tasks, the customer
 * party book and the org address book into the panels' `DashboardOrder`
 * read-model. Tasks are sorted by `sequence` so the earliest leg reads first.
 */
export function buildDashboardOrders(
  orders: readonly Order[],
  tasksByOrderId: Map<string, OrderTask[]>,
  customersById: Map<string, Party>,
  addressById: Map<string, Address>,
): DashboardOrder[] {
  return orders.map((order) => {
    const rawTasks = (tasksByOrderId.get(order.id) ?? [])
      .slice()
      .sort((a, b) => a.sequence - b.sequence);

    const tasks: DashboardTask[] = rawTasks.map((task) => {
      const address = addressById.get(task.addressId);
      return {
        scheduledDate: task.plannedDate ?? null,
        timeFrom: trimTime(task.plannedTime),
        locationName: address?.name ?? null,
        city: address?.city ?? null,
      };
    });

    const earliestPlanned = rawTasks
      .map((t) => t.plannedDate)
      .filter((d): d is string => !!d)
      .sort()[0];

    return {
      id: order.id,
      jobNumber: order.orderNumber,
      status: order.status,
      customer: customersById.get(order.customerPartyId)?.name ?? 'Unknown customer',
      serviceDate: earliestPlanned ?? null,
      createdAt: order.createdUtc,
      tasks,
      missingPod: rawTasks.some(
        (t) => t.podStatus === 'Required' || t.podStatus === 'Pending',
      ),
    };
  });
}

// ─── Derived predicates + metrics ────────────────────────────────────────────

/** Active order with nothing scheduled — the "needs attention" set. */
export function isUnscheduled(order: DashboardOrder): boolean {
  return ACTIVE_STATUSES.includes(order.status) && order.tasks.length === 0;
}

export function hasTaskToday(order: DashboardOrder, todayIso: string): boolean {
  return order.tasks.some((t) => t.scheduledDate === todayIso);
}

/** Earliest "HH:mm" scheduled today, or a high sentinel for stable sorting. */
export function earliestTodayTime(order: DashboardOrder, todayIso: string): string {
  return order.tasks.find((t) => t.scheduledDate === todayIso)?.timeFrom ?? '99:99';
}

export interface DashboardMetrics {
  todayTotal: number;
  pending: number;
  inProgress: number;
  completed: number;
  /** Active orders with no tasks planned yet. */
  unscheduled: number;
  /** Today's orders still awaiting a POD signature. */
  missingPod: number;
  total: number;
  countsByStatus: Record<OrderStatusValue, number>;
}

function emptyCounts(): Record<OrderStatusValue, number> {
  return DASHBOARD_STATUS_ORDER.reduce(
    (acc, status) => {
      acc[status] = 0;
      return acc;
    },
    {} as Record<OrderStatusValue, number>,
  );
}

export function computeDashboardMetrics(
  orders: DashboardOrder[],
  todayIso: string,
): DashboardMetrics {
  const todayOrders = orders.filter((o) => hasTaskToday(o, todayIso));
  const todayBy = (status: OrderStatusValue) =>
    todayOrders.filter((o) => o.status === status).length;

  const counts = emptyCounts();
  for (const o of orders) counts[o.status] += 1;

  return {
    todayTotal: todayOrders.length,
    pending: todayOrders.filter((o) => o.status === 'Draft' || o.status === 'Open').length,
    inProgress: todayBy('InProgress'),
    completed: todayBy('Completed'),
    unscheduled: orders.filter(isUnscheduled).length,
    missingPod: todayOrders.filter((o) => o.missingPod).length,
    total: orders.length,
    countsByStatus: counts,
  };
}
